import React from "react";
import { Modal, Badge } from "react-bootstrap";
import {
  Database,
  Code,
  Eye,
  FileText,
  Variable,
  Settings,
  Calendar,
  CheckCircle,
  XCircle,
  Pencil,
  X,
} from "lucide-react";
import { Button } from "components/ui/button";
import { TYPE_COLORS } from "services/constants";
import localization from "services/localization";

export default function ViewPromptTemplateModal({
  show,
  item,
  onEdit,
  onClose,
}) {
  if (!item) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="xl"
      backdrop="static"
      className="themed-modal addEditModal"
    >
      <Modal.Header closeButton className="border-bottom">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2">
            <span className="font-bold">{item.name}</span>
            <span
              className={`
        text-xs capitalize border px-2 py-1 rounded-full 
        ${TYPE_COLORS[item.type] || "bg-gray-100 text-gray-600 border-gray-300"}
    `}
            >
              {item.type}
            </span>
          </h5>
          <small className="description-text">{item.description}</small>
        </div>
      </Modal.Header>

      <Modal.Body
        style={{ maxHeight: "75vh", overflowY: "auto", padding: "2rem" }}
      >
        <div className="d-grid gap-4">
          {/* Overview Boxes */}
          <div className="row g-3 mb-4">
            <div className="col-4">
              <div className="overview-card d-flex gap-2 p-3 rounded">
                <Database size={16} className="text-primary" />
                <div>
                  <div className="card-label small">
                    {localization.rag || "RAG"}
                  </div>
                  <div className="card-value fw-semibold">
                    {item.rag ? "Enabled" : "Disabled"}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-4">
              <div className="overview-card p-3 rounded border bg-light d-flex gap-2">
                <Code size={16} className="text-success" />
                <div>
                  <div className="card-label small">
                    {localization.format || "Format"}
                  </div>
                  <div className="card-value fw-semibold">
                    {item.html_format ? "HTML" : "Plain"}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-4">
              <div className="overview-card p-3 rounded border bg-light d-flex gap-2">
                <Eye size={16} style={{ color: "#6f42c1" }} />
                <div>
                  <div className="card-label small">
                    {localization.fields || "Fields"}
                  </div>
                  <div className="card-value fw-semibold">
                    {item.system_props?.viz_props?.length || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr />

          {/* System Prompt */}
          <div className="mb-4 mt-4">
            <div className="d-flex align-items-center gap-2 mb-3 border-start ps-2">
              <FileText size={18} style={{ color: "#6f42c1" }} />
              <span className="mb-0 fw-semibold" style={{ fontSize: "20px" }}>
                {localization.system_prompt || "System Prompt"}
              </span>
            </div>

            <div className="system-prompt-box p-3 border rounded">
              <pre className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {item.system_prompt}
              </pre>
            </div>
          </div>

          <hr />

          {/* Instruction Variables */}
          <div className="mb-4 mt-4">
            <div className="d-flex align-items-center gap-2 mb-3 border-start ps-2">
              <Variable size={18} className="text-primary" />
              <span className="mb-0 fw-semibold" style={{ fontSize: "20px" }}>
                {localization.instruction_variables || "Instruction Variables"}
              </span>
            </div>

            {item.inputs?.instruction_variables?.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {item.inputs.instruction_variables.map((variable) => (
                  <Badge key={variable} bg="secondary" className="text-white">
                    {variable}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-muted small">
                {localization.no_variables_defined || "No variables defined"}
              </div>
            )}
          </div>

          <hr />

          {/* Viz Props */}
          <div className="mb-4 mt-4">
            <div className="d-flex align-items-center gap-2 mb-3 border-start ps-2">
              <Settings size={18} className="text-success" />
              <span className="mb-0 fw-semibold" style={{ fontSize: "20px" }}>
                {localization.input_fields || "Input Fields"}
              </span>
            </div>

            {item.system_props?.viz_props?.length > 0 ? (
              <div className="d-grid gap-3">
                {item.system_props.viz_props.map((prop, i) => (
                  <div
                    key={i}
                    className="viz-prop-card p-3 border rounded  d-flex gap-3"
                  >
                    <div className="fs-4 input-type-icon">
                      {prop.inputType === "textarea" && "📝"}
                      {prop.inputType === "text" && "✏️"}
                      {prop.inputType === "date" && "📅"}
                      {prop.inputType === "number" && "🔢"}
                      {prop.inputType === "select" && "📋"}
                      {prop.inputType === "file-uploader" && "📎"}
                      {prop.inputType === "student-list" && "👥"}
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <strong className="main-title">{prop.label}</strong>
                        <Badge
                          bg="light"
                          text="dark"
                          className="input-type-badge border text-uppercase small"
                        >
                          {prop.inputType}
                        </Badge>

                        {prop.required ? (
                          <CheckCircle size={14} className="text-success" />
                        ) : (
                          <XCircle size={14} className="text-muted" />
                        )}
                      </div>

                      <div className="small field-description">
                        {prop.description}
                      </div>

                      <code className="small field-name">
                        {localization.name || "Name"}: {prop.name}
                      </code>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="description-text small">
                {localization.no_input_fields_defined ||
                  "No input fields defined"}
              </div>
            )}
          </div>

          <hr />

          {/* Timestamps */}
          <div className="d-flex gap-4 small description-text">
            <div className="d-flex align-items-center gap-1">
              <Calendar size={14} />
              {localization.created_at || "Created at"}:{" "}
              {new Date(item.created_at).toLocaleString()}
            </div>

            <div className="d-flex align-items-center gap-1">
              <Calendar size={14} />
              {localization.updated_at || "Updated at"}:{" "}
              {new Date(item.updated_at).toLocaleString()}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" className="text-white" onClick={onClose}>
          <X className="mr-2" size={16} />
          {localization.cancel || "Cancel"}
        </Button>

        <Button
          variant="default"
          className="text-white"
          onClick={() => onEdit(item)}
        >
          <Pencil className="mr-2" size={16} />
          {localization.edit_template || "Edit Template"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
